"use client";

import { useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { CheckCircle2, X } from "lucide-react";
import { useAnimatedOpen } from "@/components/ui/use-animated-open";
import { useT } from "@/lib/use-i18n";
import type { TranslationKey } from "@/lib/i18n/locales";

const FLASH_MESSAGES: Record<string, TranslationKey> = {
  email_confirmed: "authFlashEmailConfirmed",
  password_email_ok: "authFlashPasswordEmailOk",
  signed_in: "authFlashSignedIn",
  password_updated: "authFlashPasswordUpdated",
  signup_disabled: "authErrorSignupDisabled",
};

/**
 * Beautiful post-auth confirmation banner when returning from email links
 * (?auth=email_confirmed | password_email_ok | signed_in).
 *
 * Lives in root layout (SPA-persistent). Dismiss only hides the *current* URL flash;
 * when `auth` is cleared from the URL we reset dismiss so the same flash type can
 * reappear later in the session (e.g. second email confirmation).
 */
export function AuthFlashBanner() {
  const t = useT();
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const flash = searchParams.get("auth") ?? "";
  const key = FLASH_MESSAGES[flash];
  const [dismissedFlash, setDismissedFlash] = useState<string | null>(null);

  // Adjust state while rendering when the auth param is gone — preferred over
  // setState-in-effect (React docs: store info from previous renders).
  // Without this, layout-mounted banner never re-shows the same flash type.
  if (!flash && dismissedFlash !== null) {
    setDismissedFlash(null);
  }

  const visible = Boolean(key) && dismissedFlash !== flash;

  const { shouldRender, isLeaving } = useAnimatedOpen(visible, 300);

  useEffect(() => {
    if (!key || !visible) return;
    // 2026-09-22 FASE 3 FIX P3 a11y: el auto-dismiss a 9s se anuncia vía el
    // role="status" pre-existente del contenedor (live-region polite
    // implícita) — sin aria-live adicional ni foco robado (research 2026
    // WCAG 2.2 4.1.3: status ya anuncia cambios sin interrumpir).
    const timer = window.setTimeout(() => {
      dismiss();
    }, 9000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dismiss closes over latest router
  }, [key, visible, flash]);

  if (!key || !shouldRender) return null;

  function dismiss() {
    setDismissedFlash(flash);
    const params = new URLSearchParams(searchParams.toString());
    params.delete("auth");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }

  return (
    <div
      role="status"
      className="fixed inset-x-0 top-4 z-[80] flex justify-center px-4 pointer-events-none"
      data-auth-flash={flash}
    >
      {/* 2026-09-21 FASE 3 FIX design-auth-admin: sin backdrop-blur-md propio —
          lovable-card ya aporta la superficie (doble blur = 2 filtros GPU). */}
      <div
        className={`pointer-events-auto flex max-w-lg items-start gap-3 lovable-card lovable-card--static px-4 py-3.5 ${isLeaving ? "animate-out fade-out slide-out-to-top-2 duration-300" : "animate-in fade-in slide-in-from-top-2 duration-300"}`}
      >
        <div className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-xl bg-[var(--success)]/15 text-[var(--success)]">
          <CheckCircle2 className="size-5" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <p className="text-sm font-semibold text-foreground">{t(key)}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {t("authFlashSub")}
          </p>
        </div>
        {/* 2026-09-22 FASE 3 FIX P2 a11y 2.5.8 (research 2026 WCAG 2.2 target
            ≥24px + paridad BetaBanner:85-89): p-1.5 + size-4 = 28px ≥24px;
            min-h-6/min-w-6 garantizan el target aunque el icono cambie.
            aria-label con key propia authFlashDismiss (no cancelConfirmNo). */}
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg min-h-6 min-w-6 p-1.5 text-muted-foreground hover:bg-[var(--muted)] hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2"
          aria-label={t("authFlashDismiss")}
        >
          <X className="size-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
